import { useMemo, useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye } from "lucide-react";
import { useI18n, useT } from "../../i18n/I18nProvider";
import type { AccountUser } from "../../lib/auth";
import type { PortalSummary } from "../../lib/portal";
import { portalLinks } from "../../lib/brand";
import type { TabKey } from "../tabs";
import { ShellContext, type ShellContextValue } from "./context";
import { personalNav, type NavBadges, type NavItem } from "./nav";
import { PersonalNav } from "./PersonalNav";
import { PortalHeader } from "./PortalHeader";
import { PortalTabBar } from "./PortalTabBar";

/**
 * The client portal's chrome: a calm page, one navigation, and the document.
 *
 * The same screens the hub draws, in a frame built for somebody who is NOT here
 * to manage a product. No animated backdrop and no side column: a ledger and a
 * contract are read against a still, plain ground, and the width the side menu
 * took is the width an invoice table needs.
 *
 * The layout decides who is looking and what they may open; this file only
 * draws it. In a staff preview it draws exactly what the client would see,
 * under a banner that says so.
 */
export function PortalShell({
  user,
  preview,
  summary,
  crmLink,
  active,
  nav,
  badges,
  menuTabs,
  onGo,
  onLogout,
  children,
}: {
  /** Null only in a staff preview - see `AccountLayout`. */
  user: AccountUser | null;
  preview: boolean;
  summary: PortalSummary | null;
  crmLink: boolean;
  active: TabKey;
  /** The overview and the services this contact may open, in menu order. */
  nav: NavItem[];
  badges: NavBadges;
  /** The personal screens the avatar menu may open. */
  menuTabs: TabKey[];
  onGo: (tab: TabKey) => void;
  onLogout: () => void;
  /** The screen behind the active tab. */
  children: ReactNode;
}) {
  const t = useT();
  const { locale } = useI18n();
  // A document page has pinned its action to the bottom edge of a phone, so
  // the tab bar gives the edge up for as long as that page is open.
  const [docBar, setDocBar] = useState(false);
  const shell = useMemo<ShellContextValue>(() => ({ variant: "portal", setDocBar }), []);
  const links = portalLinks(locale);
  const clients = summary?.clients ?? [];
  // One company is who this person is here; several is a list, and a list does
  // not fit under a name. The overview spells them out instead.
  const caption = clients.length === 1 ? clients[0]!.name : undefined;
  // On one of the person's own screens, the list of all of them; elsewhere none.
  const personal = menuTabs.includes(active) ? personalNav(menuTabs) : [];

  const screen = (
    <AnimatePresence mode="wait">
      {/* The account page is one page whichever of its screens the URL names,
          so moving between them scrolls it instead of fading it out and in. */}
      <motion.div
        key={personal.length > 0 ? "personal" : active}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.16 }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );

  return (
    <ShellContext.Provider value={shell}>
      <div className="flex min-h-dvh flex-col bg-background">
        {/* For a keyboard: past the bar and its seven links, straight to the
            page. Invisible until it is focused. */}
        <a
          href="#portal-main"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-card focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:shadow-floating"
        >
          {t("portal.shell.skip")}
        </a>
        <PortalHeader
          user={preview ? null : user}
          crmLink={crmLink}
          active={active}
          nav={nav}
          badges={badges}
          menuTabs={menuTabs}
          caption={caption}
          onGo={onGo}
          onLogout={onLogout}
        />

        <main id="portal-main" tabIndex={-1} className="mx-auto w-full max-w-6xl flex-1 px-4 pb-12 pt-6 outline-none md:pt-10">
          {/* ── The preview banner ─────────────────────────────────────────
              It says three things, and all three matter: this is not the
              operator's own portal, whose portal it is, and that nothing here
              can be changed. An operator who forgets which of the two they are
              looking at is the failure this whole mode has to design against. */}
          {preview && (
            <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-primary/40 bg-primary/10 p-4 text-sm">
              <Eye className="size-5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{t("preview.title")}</p>
                <p className="mt-0.5 text-foreground/70">
                  {clients[0]?.name ? t("preview.forClient", { name: clients[0].name }) : t("preview.body")}
                </p>
              </div>
            </div>
          )}

          {/* The person's own screens get their list beside them. Without it a
              form of three fields ran the full width of the page, and moving
              from the profile to the security screen meant the avatar menu
              again. The list stays put while the screen beside it changes. */}
          {personal.length > 0 ? (
            // `minmax(0,1fr)` on a phone too: an implicit grid column grows to
            // the full width of the chip row, and the page scrolled sideways.
            // Under `lg` the chip row is pulled up over the page's top padding,
            // so it sits right under the header before it starts to stick, not
            // behind a strip of empty page. A preview keeps the gap under its
            // banner.
            <div
              className={`grid grid-cols-[minmax(0,1fr)] items-start gap-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-10 ${
                preview ? "" : "-mt-6 md:-mt-10 lg:mt-0"
              }`}
            >
              <PersonalNav items={personal} active={active} onGo={onGo} />
              <div className="min-w-0 max-w-3xl">{screen}</div>
            </div>
          ) : (
            screen
          )}
        </main>

        {/* The legal texts and a way to reach a person. Bottom padding under
            `lg` clears whichever bar is pinned to the edge - the tab bar or a
            document's action - so the last link is never underneath it. */}
        <footer className="border-t border-border print:hidden">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 pb-24 pt-5 text-sm text-foreground/70 lg:pb-6">
            <span>© {new Date().getFullYear()} WebAdmin</span>
            <span className="flex flex-wrap gap-x-5 gap-y-1">
              <a className="underline-offset-4 hover:text-foreground hover:underline" href={links.privacy} target="_blank" rel="noreferrer">
                {t("portal.shell.privacy")}
              </a>
              <a className="underline-offset-4 hover:text-foreground hover:underline" href={links.terms} target="_blank" rel="noreferrer">
                {t("portal.shell.terms")}
              </a>
              <a className="underline-offset-4 hover:text-foreground hover:underline" href={links.contact} target="_blank" rel="noreferrer">
                {t("portal.shell.contact")}
              </a>
            </span>
          </div>
        </footer>

        <PortalTabBar active={active} nav={nav} badges={badges} hidden={docBar} onGo={onGo} />
      </div>
    </ShellContext.Provider>
  );
}
